import { useState, useRef } from "react";
import ToolShell from "./ToolShell";
import ToolExportButton from "./ToolExportButton";
import { estimateNightly, estimateOccupancy, monthlyGross, netMonthly, fmt } from "./calculatorData";

const inputStyle = { border: "1.5px solid #E5E5E5", borderRadius: 10, padding: "13px 15px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#1A1A1A", background: "#fff", width: "100%", boxSizing: "border-box", outline: "none", transition: "border-color 0.2s" };
const labelStyle = { fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "#717171", display: "block", marginBottom: 7 };
const cardStyle = { background: "#fff", border: "1px solid #EBEBEB", borderRadius: 18, padding: 28, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" };

export default function LongLetVsShortLetCalculator() {
  const [rent, setRent] = useState(1250);
  const [city, setCity] = useState("Manchester");
  const [beds, setBeds] = useState("2 beds");
  const reportRef = useRef(null);

  const nightly = estimateNightly(city, beds, "Apartment");
  const occupancy = estimateOccupancy(city);
  const gross = monthlyGross(nightly, occupancy);
  const net = netMonthly(gross);
  const upliftMonthly = net - rent;
  const upliftAnnual = upliftMonthly * 12;
  const upliftPct = rent > 0 ? Math.round(((net - rent) / rent) * 100) : 0;

  const summary = `Long-let vs Short-let — City: ${city}, ${beds}. Current long-let rent: ${fmt(rent)}/mo. Short-let monthly net: ${fmt(net)}. Monthly uplift: ${fmt(upliftMonthly)} (${upliftPct}%). Annual uplift: ${fmt(upliftAnnual)}.`;

  return (
    <ToolShell
      title="Long-let vs Short-let Calculator"
      tagline="Compare your current rental income against short-let earnings — see the monthly and annual difference side by side."
      description="Free long-let vs short-let calculator. Compare your AST rental income against short-let earnings by city and bedrooms. Request a personalised comparison report."
      slug="long-let-vs-short-let-calculator"
    >
      <div className="calculator-grid" style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: 40, alignItems: "start" }}>
        <div style={cardStyle}>
          <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 17, color: "#1A1A1A", marginTop: 0, marginBottom: 20 }}>Your property details</p>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Current monthly rent (long-let)</label>
            <input style={inputStyle} type="number" min="0" value={rent} onChange={e => setRent(Math.max(0, Number(e.target.value) || 0))} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>City</label>
            <input style={inputStyle} type="text" value={city} onChange={e => setCity(e.target.value)} placeholder="e.g. Manchester" />
          </div>
          <div>
            <label style={labelStyle}>Bedrooms</label>
            <select style={inputStyle} value={beds} onChange={e => setBeds(e.target.value)}>
              {["Studio", "1 bed", "2 beds", "3 beds", "4+ beds"].map(b => <option key={b}>{b}</option>)}
            </select>
          </div>
        </div>

        <div>
          <ToolExportButton slug="long-let-vs-short-let-calculator" title="Long-let vs Short-let" targetRef={reportRef} fileName={`Long-let-vs-Short-let-${city}`} />
          <div ref={reportRef} style={{ background: "#fff", padding: 24, borderRadius: 16, border: "1px solid #EBEBEB" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 0 16px", borderBottom: "1px solid #F0F0F0", marginBottom: 18 }}>
              <div>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 17, color: "#1A1A1A", margin: 0 }}>Long-let vs Short-let — {city}</p>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#888", margin: "4px 0 0" }}>{beds}</p>
              </div>
              <img src="https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/24682ceeb_TruestaysLogo.png" alt="Truestays" style={{ height: 30, width: "auto" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              <div style={{ background: "#F7F7F7", borderRadius: 14, padding: "18px 20px", border: "1px solid #F0F0F0" }}>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#717171", margin: "0 0 7px" }}>Long-let (current)</p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 26, color: "#1A1A1A", margin: 0, letterSpacing: "-0.02em" }}>{fmt(rent)}<span style={{ fontSize: 14, fontWeight: 500, color: "#717171" }}>/mo</span></p>
              </div>
              <div style={{ background: "#F0F9F4", borderRadius: 14, padding: "18px 20px", border: "1px solid #E0F0E8" }}>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#1B8B5E", margin: "0 0 7px" }}>Short-let (net)</p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 26, color: "#1A1A1A", margin: 0, letterSpacing: "-0.02em" }}>{fmt(net)}<span style={{ fontSize: 14, fontWeight: 500, color: "#717171" }}>/mo</span></p>
              </div>
            </div>

            <div style={{ background: "#F7F7F7", borderRadius: 16, padding: 32, marginBottom: 0, border: "1px solid #E5E5E5" }}>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#717171", margin: "0 0 14px" }}>Your additional income</p>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 20 }}>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: "clamp(36px, 5vw, 52px)", color: "#1A1A1A", margin: 0, letterSpacing: "-0.03em" }}>+{fmt(upliftMonthly)}</p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 20, color: "#1B8B5E", margin: 0 }}>/month</p>
              </div>
              <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
                <div>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#717171", margin: "0 0 3px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Annual uplift</p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 19, color: "#1A1A1A", margin: 0 }}>+{fmt(upliftAnnual)}</p>
                </div>
                <div>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#717171", margin: "0 0 3px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Income increase</p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 19, color: "#1B8B5E", margin: 0 }}>+{upliftPct}%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 900px) { .calculator-grid { grid-template-columns: 1fr !important; } }`}</style>
    </ToolShell>
  );
}